import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { readableColor } from 'polished';
import { useSelector } from '@/redux';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { useLocation } from '@/features/locations/service';
import { isAsapAvailable, isAsapValue } from '@/utils/conveyance';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { safePolished } from '@/utils/safePolished';
import { ClockIcon } from '@/components/uielements/icon/clock';
import { getSafeHoverColor } from '@/components/uielements/primaryButton/utils';
import { LAYOUT } from '@/constants/styles';

const NOTIFICATION_BAR_HEIGHT_VAR = '--notification-bar-height';

function useNotificationBarHeight() {
  const ref = useRef<HTMLDivElement>(null);

  const updateHeight = useCallback(() => {
    const height = ref.current?.offsetHeight ?? 0;
    document.documentElement.style.setProperty(NOTIFICATION_BAR_HEIGHT_VAR, `${height}px`);
  }, []);

  useEffect(() => {
    updateHeight();

    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(NOTIFICATION_BAR_HEIGHT_VAR, '0px');
    };
  }, [updateHeight]);

  return ref;
}

export function ConveyanceNotificationBar() {
  const { time_wanted, location } = useSelector(selectConveyance);
  const { conveyanceMode } = useSelector((state) => state.app);
  const { data: storeLocation } = useLocation(location);

  const noTimeSelected =
    !isAsapAvailable(storeLocation?.data, conveyanceMode.type) && isAsapValue(time_wanted);
  const barRef = useNotificationBarHeight();

  if (!noTimeSelected) {
    return null;
  }

  const handleSelectTime = () => {
    const conveyanceButton =
      document.getElementById('pickup-conveyance-button') ??
      document.getElementById('delivery-conveyance-button');
    conveyanceButton?.click();
  };

  return (
    <Bar ref={barRef}>
      <Message>
        <ClockIcon />
        No ASAP times available today. Select a time to place your order.
      </Message>
      <SelectTimeButton onClick={handleSelectTime} size="small">
        Select a time
      </SelectTimeButton>
    </Bar>
  );
}

const Bar = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 10px',
  backgroundColor: theme.global.primary_active_color,
  position: 'sticky',
  top: '6.7rem',
  zIndex: 1,
  flexDirection: 'column',

  '@media (min-width: 768px)': {
    padding: '16px 10px',
    flexDirection: 'row',
    zIndex: 9,
    top: '8.5rem',
  },

  '@media (min-width: 1280px)': {
    top: LAYOUT.HEADERHEIGHT,
  },
}));

const Message = styled.span(({ theme }) => ({
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '24px',
  color: safePolished(readableColor)(theme.global.primary_active_color),
  display: 'flex',
  gap: '6px',
  alignItems: 'flex-start',

  '@media (min-width: 768px)': {
    alignItems: 'center',
    fontSize: '16px',
  },
}));

const SelectTimeButton = styled(StyledPrimaryButton)(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontSize: '16px',
  backgroundColor: theme.header.border_color ?? '#ddd',
  color: safePolished(readableColor)(theme.header.font.color),
  width: '100%',

  '&:hover': {
    background: getSafeHoverColor(null, theme.header.border_color ?? '#ddd'),
    borderColor: getSafeHoverColor(null, theme.header.border_color ?? '#ddd'),
    color: safePolished(readableColor)(theme.header.font.color),
  },

  '@media (min-width: 768px)': {
    width: 'auto',
  },
}));
